import styled from '@emotion/styled';
import { NavLink } from 'react-router';

// ---------------------------------------------------------------------------
// Layout
// ---------------------------------------------------------------------------

export const LayoutContainer = styled.div({
  display: 'flex',
  flexDirection: 'row',
  width: '100%',
  height: '100%',
  background: 'var(--bg-base)',
});

export const Sidebar = styled.nav({
  width: 'var(--sidebar-width)',
  height: '100vh',
  background: 'var(--bg-surface)',
  borderRight: '1px solid var(--bg-border)',
  display: 'flex',
  flexDirection: 'column',
  padding: '20px 12px',
  flexShrink: 0,
  position: 'sticky',
  top: 0,
});

export const Wordmark = styled.div({
  marginBottom: 8,
  paddingLeft: 4,
  display: 'flex',
  alignItems: 'center',
  userSelect: 'none',
});

export const NavList = styled.ul({
  listStyle: 'none',
  margin: 0,
  padding: 0,
  flex: 1,
});

export const NavItem = styled.li({ margin: 0, padding: '1px 0' });

export const StyledNavLink = styled(NavLink)({
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  padding: '9px 12px',
  borderRadius: 6,
  fontSize: 13,
  fontWeight: 500,
  fontFamily: "'JetBrains Mono', monospace",
  color: 'var(--text-secondary)',
  textDecoration: 'none',
  transition: 'background 150ms ease, color 150ms ease',
  '&:hover': { background: 'var(--bg-surface-hover)', color: 'var(--text-primary)', textDecoration: 'none' },
  '&.active': { background: 'var(--accent-cyan-glow)', color: 'var(--accent-cyan)' },
});

export const NavUser = styled.div({
  marginTop: 'auto',
  padding: '12px',
  borderRadius: 6,
  borderTop: '1px solid var(--bg-border)',
  paddingTop: 16,
});

export const NavUserEmail = styled.div({
  fontSize: 12,
  color: 'var(--text-secondary)',
  marginBottom: 8,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
});

export const LayoutMain = styled.main({
  flex: 1,
  minHeight: '100vh',
  overflowY: 'auto',
  padding: 'var(--page-padding-y) var(--page-padding-x)',
  display: 'flex',
  flexDirection: 'column',
});

export const PageHeader = styled.div({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: 'var(--section-gap)',
});

export const PageTitle = styled.h1({
  margin: 0,
  fontSize: 16,
  fontWeight: 600,
  fontFamily: "'JetBrains Mono', monospace",
  color: 'var(--text-primary)',
});

// ---------------------------------------------------------------------------
// Cards
// ---------------------------------------------------------------------------

export const Card = styled.div({
  background: 'var(--bg-surface)',
  border: '1px solid var(--bg-border)',
  borderRadius: 'var(--radius-card)',
  padding: 'var(--card-padding)',
  transition: 'border-color 150ms ease, box-shadow 150ms ease',
  '&:hover': {
    borderColor: 'rgba(0,200,117,0.3)',
    boxShadow: '0 0 0 1px rgba(0,200,117,0.08)',
  },
});

export const CardGrid = styled.div<{ cols?: number }>(({ cols = 4 }) => ({
  display: 'grid',
  gridTemplateColumns: `repeat(${cols}, 1fr)`,
  gap: 16,
}));

// ---------------------------------------------------------------------------
// Buttons
// ---------------------------------------------------------------------------

const btnBase = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  border: 'none',
  borderRadius: 'var(--radius-btn)',
  padding: '8px 16px',
  fontSize: 14,
  fontWeight: 600,
  cursor: 'pointer',
  transition: 'opacity 150ms ease, transform 80ms ease, border-color 150ms ease, background 150ms ease',
  '&:disabled': { opacity: 0.4, cursor: 'default' },
  '&:active:not(:disabled)': { transform: 'scale(0.97)' },
} as const;

export const PrimaryButton = styled.button({
  ...btnBase,
  background: 'var(--text-primary)',
  color: 'var(--bg-base)',
  '&:hover:not(:disabled)': { opacity: 0.88 },
});

export const SecondaryButton = styled.button({
  ...btnBase,
  background: 'transparent',
  border: '1px solid var(--bg-border)',
  color: 'var(--text-primary)',
  fontWeight: 500,
  '&:hover:not(:disabled)': { borderColor: 'var(--accent-cyan)', color: 'var(--accent-cyan)' },
});

export const DestructiveButton = styled.button({
  ...btnBase,
  background: 'transparent',
  border: '1px solid var(--accent-red)',
  color: 'var(--accent-red)',
  fontWeight: 500,
  '&:hover:not(:disabled)': { background: 'var(--accent-red-glow)' },
});

export const GhostButton = styled.button({
  ...btnBase,
  background: 'transparent',
  border: 'none',
  color: 'var(--text-secondary)',
  padding: '6px 10px',
  fontWeight: 500,
  '&:hover:not(:disabled)': { color: 'var(--text-primary)', background: 'var(--bg-surface-hover)' },
});

// Exported as StyledButton for backward compat
export const StyledButton = PrimaryButton;

// ---------------------------------------------------------------------------
// Form inputs
// ---------------------------------------------------------------------------

const inputBase = {
  background: 'var(--bg-base)',
  border: '1px solid var(--bg-border)',
  borderRadius: 'var(--radius-btn)',
  color: 'var(--text-primary)',
  fontFamily: 'inherit',
  padding: '8px 12px',
  fontSize: 14,
  width: '100%',
  outline: 'none',
  transition: 'border-color 150ms ease, box-shadow 150ms ease',
  colorScheme: 'dark',
  '&::placeholder': { color: 'var(--text-muted)' },
  '&:focus': {
    borderColor: 'var(--accent-cyan)',
    boxShadow: '0 0 0 3px var(--accent-cyan-glow)',
    outline: 'none',
  },
  '&::-webkit-calendar-picker-indicator': { filter: 'invert(0.6) sepia(1) hue-rotate(160deg)' },
} as const;

export const Input = styled.input(inputBase);
export const StyledInput = Input;
export const Textarea = styled.textarea({ ...inputBase, resize: 'vertical', minHeight: 80 });
export const Select = styled.select({
  ...inputBase,
  appearance: 'none',
  backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%23888' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E")`,
  backgroundRepeat: 'no-repeat',
  backgroundPosition: 'right 12px center',
  paddingRight: 32,
  cursor: 'pointer',
});

// ---------------------------------------------------------------------------
// Table
// ---------------------------------------------------------------------------

export const Table = styled.table({
  width: '100%',
  borderCollapse: 'collapse',
});

export const Thead = styled.thead({
  '& th': {
    fontSize: 11,
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    color: 'var(--text-secondary)',
    padding: '10px 12px 10px',
    textAlign: 'left',
    borderBottom: '1px solid var(--bg-border)',
  },
});

export const Tbody = styled.tbody({
  '& tr': {
    borderBottom: '1px solid var(--bg-border)',
    transition: 'background 100ms ease',
    cursor: 'pointer',
  },
  '& tr:hover': { background: 'var(--bg-surface-hover)' },
  '& td': {
    padding: '0 16px',
    height: 'var(--table-row-height)',
    fontSize: 14,
    color: 'var(--text-primary)',
  },
});

// ---------------------------------------------------------------------------
// Badges
// ---------------------------------------------------------------------------

const badgeBase = {
  display: 'inline-flex',
  alignItems: 'center',
  borderRadius: 'var(--radius-badge)',
  padding: '2px 8px',
  fontSize: 12,
  fontWeight: 600,
  letterSpacing: '0.02em',
} as const;

export const Badge = styled.span<{ variant?: string }>(({ variant = 'default' }) => ({
  ...badgeBase,
  ...(variant === 'active' && { color: 'var(--accent-green)', background: 'var(--accent-green-glow)' }),
  ...(variant === 'paused' && { color: 'var(--accent-amber)', background: 'var(--accent-amber-glow)' }),
  ...(variant === 'error' || variant === 'deactivated' ? { color: 'var(--accent-red)', background: 'var(--accent-red-glow)' } : {}),
  ...(variant === 'invited' && { color: 'var(--text-secondary)', background: 'rgba(107,128,153,0.12)' }),
  ...(variant === 'inbound' && { color: 'var(--accent-cyan)', background: 'var(--accent-cyan-glow)' }),
  ...(variant === 'outbound' && { color: 'var(--text-secondary)', background: 'rgba(107,128,153,0.12)' }),
  ...(variant === 'expired' && { color: 'var(--text-muted)', background: 'rgba(61,80,104,0.2)' }),
  ...(variant === 'default' && { color: 'var(--text-secondary)', background: 'rgba(107,128,153,0.12)' }),
}));

// ---------------------------------------------------------------------------
// Skeleton
// ---------------------------------------------------------------------------

export const Skeleton = styled.div<{ width?: string; height?: number }>(
  ({ width = '100%', height = 16 }) => ({
    width,
    height,
    background: 'linear-gradient(90deg, var(--bg-surface) 25%, var(--bg-surface-hover) 50%, var(--bg-surface) 75%)',
    backgroundSize: '200% 100%',
    borderRadius: 4,
    animation: 'shimmer 1.5s infinite',
  })
);

export const SkeletonRow = styled.tr({
  '& td': { padding: '12px 16px' },
});

// ---------------------------------------------------------------------------
// Modal
// ---------------------------------------------------------------------------

export const ModalOverlay = styled.div({
  position: 'fixed',
  inset: 0,
  background: 'rgba(8, 13, 26, 0.8)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 1000,
  animation: 'fadeIn 200ms ease',
});

export const ModalDialog = styled.div({
  background: 'var(--bg-surface)',
  border: '1px solid var(--bg-border)',
  borderRadius: 10,
  padding: 28,
  width: 480,
  maxWidth: 'calc(100vw - 48px)',
  animation: 'modalIn 200ms cubic-bezier(0.4, 0, 0.2, 1)',
});

export const ModalTitle = styled.h2({
  margin: '0 0 12px',
  fontSize: 18,
  fontWeight: 600,
  color: 'var(--text-primary)',
});

export const ModalBody = styled.p({
  margin: '0 0 24px',
  fontSize: 14,
  color: 'var(--text-secondary)',
  lineHeight: 1.6,
});

export const ModalActions = styled.div({
  display: 'flex',
  gap: 10,
  justifyContent: 'flex-end',
});

// ---------------------------------------------------------------------------
// Form layout helpers
// ---------------------------------------------------------------------------

export const FormGroup = styled.div({
  marginBottom: 16,
  '& label': {
    display: 'block',
    fontSize: 12,
    fontWeight: 600,
    color: 'var(--text-secondary)',
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
    marginBottom: 6,
  },
});

export const FormRow = styled.div({
  display: 'flex',
  gap: 12,
  alignItems: 'flex-start',
});

// ---------------------------------------------------------------------------
// Status / feedback
// ---------------------------------------------------------------------------

export const InlineSuccess = styled.span({
  fontSize: 13,
  color: 'var(--accent-green)',
  animation: 'fadeIn 150ms ease',
});

export const InlineError = styled.span({
  fontSize: 13,
  color: 'var(--accent-red)',
  animation: 'fadeIn 150ms ease',
});

export const AlertBanner = styled.div<{ variant?: 'amber' | 'red' | 'cyan' | 'green' }>(
  ({ variant = 'amber' }) => ({
    padding: '10px 16px',
    borderRadius: 6,
    fontSize: 13,
    marginBottom: 16,
    ...(variant === 'amber' && {
      background: 'var(--accent-amber-glow)',
      color: 'var(--accent-amber)',
      border: '1px solid rgba(245,149,0,0.2)',
    }),
    ...(variant === 'red' && {
      background: 'var(--accent-red-glow)',
      color: 'var(--accent-red)',
      border: '1px solid rgba(224,80,80,0.2)',
    }),
    ...(variant === 'cyan' && {
      background: 'var(--accent-cyan-glow)',
      color: 'var(--accent-cyan)',
      border: '1px solid rgba(0,196,232,0.2)',
    }),
    ...(variant === 'green' && {
      background: 'var(--accent-green-glow)',
      color: 'var(--accent-green)',
      border: '1px solid rgba(0,200,117,0.25)',
    }),
  })
);

// ---------------------------------------------------------------------------
// Monospace text
// ---------------------------------------------------------------------------

export const Mono = styled.span({
  fontFamily: "'JetBrains Mono', 'Courier New', monospace",
  fontSize: 13,
});

// ---------------------------------------------------------------------------
// Tabs
// ---------------------------------------------------------------------------

export const TabBar = styled.div({
  display: 'flex',
  gap: 4,
  borderBottom: '1px solid var(--bg-border)',
  marginBottom: 24,
});

export const Tab = styled.button<{ active?: boolean }>(({ active }) => ({
  background: 'none',
  border: 'none',
  borderBottom: active ? '2px solid var(--accent-cyan)' : '2px solid transparent',
  padding: '10px 16px',
  marginBottom: -1,
  fontSize: 13,
  fontFamily: "'JetBrains Mono', monospace",
  fontWeight: active ? 600 : 400,
  color: active ? 'var(--accent-cyan)' : 'var(--text-secondary)',
  cursor: 'pointer',
  transition: 'color 150ms ease',
  '&:hover': { color: 'var(--text-primary)' },
}));

// ---------------------------------------------------------------------------
// Sign-in page
// ---------------------------------------------------------------------------

export const SignInPage = styled.div({
  width: '100%',
  height: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexDirection: 'column',
  gap: 16,
  background: 'var(--bg-base)',
  position: 'relative',
  overflow: 'hidden',
  // Wide ambient glow
  '&::before': {
    content: '""',
    position: 'absolute',
    top: '42%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 900,
    height: 700,
    background: 'radial-gradient(ellipse, rgba(0,200,117,0.05) 0%, rgba(0,200,117,0.02) 40%, transparent 68%)',
    pointerEvents: 'none',
  },
  // Tighter inner glow
  '&::after': {
    content: '""',
    position: 'absolute',
    top: '42%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 380,
    height: 380,
    background: 'radial-gradient(circle, rgba(0,200,117,0.06) 0%, transparent 70%)',
    pointerEvents: 'none',
  },
});

export const SignInWordmark = styled.div({
  display: 'flex',
  justifyContent: 'center',
  animation: 'signInWordmark 400ms ease both',
  position: 'relative',
  zIndex: 1,
});

export const SignInTagline = styled.div({
  fontFamily: "'JetBrains Mono', monospace",
  fontSize: 12,
  fontWeight: 400,
  color: 'var(--text-muted)',
  letterSpacing: '0.12em',
  animation: 'signInWordmark 400ms 80ms ease both',
  position: 'relative',
  zIndex: 1,
});

export const SignInCard = styled.div({
  background: 'rgba(8, 13, 26, 0.88)',
  backdropFilter: 'blur(48px)',
  WebkitBackdropFilter: 'blur(48px)',
  border: '1px solid rgba(0,200,117,0.15)',
  borderRadius: 16,
  padding: '40px 36px',
  width: 420,
  maxWidth: 'calc(100vw - 40px)',
  display: 'flex',
  flexDirection: 'column',
  gap: 12,
  position: 'relative',
  zIndex: 1,
  animation: 'signInCard 400ms 120ms ease both, cardPulse 6s 600ms ease-in-out infinite',
});

export const SignInTitle = styled.h2({
  fontFamily: "'Jost', sans-serif",
  margin: '0 0 6px',
  fontSize: 17,
  fontWeight: 600,
  color: 'var(--text-primary)',
  textAlign: 'center',
});

export const Divider = styled.div({
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  color: 'var(--text-muted)',
  fontSize: 12,
  '&::before, &::after': {
    content: '""',
    flex: 1,
    height: 1,
    background: 'var(--bg-border)',
  },
});

// ---------------------------------------------------------------------------
// Misc
// ---------------------------------------------------------------------------

export const SectionLabel = styled.div({
  fontSize: 11,
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: '0.08em',
  color: 'var(--text-secondary)',
  marginBottom: 12,
});

export const Muted = styled.span({ color: 'var(--text-muted)', fontSize: 12 });

export const CenteredContainer = styled.div({
  width: '100%',
  height: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
});

export const InfoMessage = styled.div({ fontSize: 14, color: 'var(--text-secondary)' });
export const ErrorMessage = styled.div({ fontSize: 14, color: 'var(--accent-red)' });

export const LayoutNav = Sidebar;
